/**
 * Platform abstraction layer.
 *
 * Web stays the default. Native (Capacitor) implementations are loaded lazily
 * via dynamic import so the web bundle stays unaffected and `npm run build`
 * works without any native SDK installed.
 */
import { PlatformService } from "@/services/platform/types";

// -----------------------------------------------------------------------------
// Platform detection
// -----------------------------------------------------------------------------

const defaultPlatform: PlatformService = {
  getPlatformType: () => 'web',
  isNative: () => false,
  getVersion: () => '1.0.0',
};

export function getPlatform(): PlatformService {
  return defaultPlatform;
}

export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  // iPadOS 13+ reports as MacIntel with touch support
  const isIPadOS =
    /Macintosh/i.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  return isIPadOS;
}

export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent);
}

/**
 * True only for real iOS Safari — excludes Chrome iOS (CriOS), Firefox iOS
 * (FxiOS), Edge iOS (EdgiOS), and common in-app webviews (FB/IG/Line).
 * Used to gate the "Add to Home Screen" coaching card.
 */
export function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (!isIOS()) return false;
  if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/i.test(ua)) return false;
  if (/FBAN|FBAV|Instagram|Line|MicroMessenger|Twitter/i.test(ua)) return false;
  return /Safari/i.test(ua);
}

/** True when the page is rendered as an installed PWA (display-mode standalone). */
export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  } catch {/* noop */}
  return !!(window.navigator as any).standalone;
}

/**
 * True when the app runs as an installed home-screen app (iOS or Android PWA).
 * Installed apps have their own storage jar, so any auth redirect that finishes
 * in the system browser can never hand the session back — sign-in must stay
 * inside this webview.
 */
export function isInstalledApp(): boolean {
  return isStandaloneDisplay();
}


/** True on Lovable preview/sandbox hosts where install prompts must not run. */
export function isLovablePreviewHost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return (
    (h.includes("preview--") && h.endsWith(".lovable.app")) ||
    h.endsWith(".lovableproject.com")
  );
}

/** True only inside a Capacitor-wrapped native shell. */
export function isNativeApp(): boolean {
  // @ts-ignore - Capacitor injects this global at runtime in native builds
  return typeof window !== 'undefined' && !!(window as any).Capacitor?.isNativePlatform?.();
}

// -----------------------------------------------------------------------------
// Share (native Share sheet → Web Share API → clipboard fallback)
// -----------------------------------------------------------------------------

export interface ShareOptions {
  title?: string;
  text?: string;
  url?: string;
}

export async function sharePayload(opts: ShareOptions): Promise<{ ok: boolean; via: 'native' | 'web' | 'clipboard' | 'none' }> {
  if (isNativeApp()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title: opts.title, text: opts.text, url: opts.url, dialogTitle: opts.title });
      return { ok: true, via: 'native' };
    } catch {
      /* fall through to web */
    }
  }
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await (navigator as any).share(opts);
      return { ok: true, via: 'web' };
    } catch {
      /* fall through to clipboard */
    }
  }
  const text = [opts.title, opts.text, opts.url].filter(Boolean).join(' — ');
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return { ok: true, via: 'clipboard' };
    } catch {
      return { ok: false, via: 'none' };
    }
  }
  return { ok: false, via: 'none' };
}

// -----------------------------------------------------------------------------
// Preferences (Capacitor Preferences on native, localStorage on web)
// -----------------------------------------------------------------------------

export const preferences = {
  async get(key: string): Promise<string | null> {
    if (isNativeApp()) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        const res = await Preferences.get({ key });
        return res.value ?? null;
      } catch {/* fall back */}
    }
    try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
    catch { return null; }
  },
  async set(key: string, value: string): Promise<void> {
    if (isNativeApp()) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({ key, value });
        return;
      } catch {/* fall back */}
    }
    try { localStorage.setItem(key, value); } catch {/* noop */}
  },
  async remove(key: string): Promise<void> {
    if (isNativeApp()) {
      try {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.remove({ key });
        return;
      } catch {/* fall back */}
    }
    try { localStorage.removeItem(key); } catch {/* noop */}
  },
};

// -----------------------------------------------------------------------------
// Status bar (no-op on web)
// -----------------------------------------------------------------------------

export async function configureStatusBar(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Default });
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch {/* noop */}
}
